<template>
  <article
    class="flex h-full flex-col overflow-hidden rounded-2xl shadow-md"
    :style="{ backgroundColor: accent }"
    data-testid="case-card"
  >
    <div class="flex flex-col gap-6 p-6 md:p-8" :class="ink.container">
      <p
        class="text-2xl font-extrabold tracking-[0.18em]"
        :class="ink.client"
        data-testid="case-card-client"
      >
        {{ client }}
      </p>
      <div class="flex flex-col gap-3">
        <p class="text-xs font-semibold tracking-[0.16em] uppercase" :class="ink.eyebrow">
          {{ eyebrow }}
        </p>
        <h3 class="text-2xl leading-tight font-bold md:text-3xl" :class="ink.title">
          {{ title }}
        </h3>
        <p class="text-sm leading-relaxed md:text-base" :class="ink.body">{{ description }}</p>
      </div>
    </div>
    <div class="mt-auto px-6 pb-6 md:px-8 md:pb-8">
      <div class="overflow-hidden rounded-xl bg-black/10 p-4 md:p-6">
        <img
          :src="image"
          :alt="imageAlt"
          loading="lazy"
          decoding="async"
          class="mx-auto block h-auto max-h-[420px] w-full object-contain"
        />
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
/**
 * `CaseCard` — постер-карточка кейса для секции `HomeCases`.
 *
 * Vertical layout: сверху бренд-цветной блок (логотип + eyebrow + h3
 * + короткое описание), снизу — обособленный фрейм с постером кейса.
 * Картинка не кропается (`object-contain` + `max-h`), вписана в
 * полупрозрачную «рамку» (`bg-black/10` поверх accent), отделена
 * паддингом от краёв карточки.
 *
 * Цвет фона приходит prop'ом `accent` и применяется через `:style`,
 * что не нарушает правило «нет inline HEX в шаблонах» — это data-binding,
 * а не литерал.
 *
 * `accentInk` решает, какой цвет «чернил» использовать:
 *   - `inverse` (default) — белый текст; подходит для тёмных и насыщенных
 *     брендов (бордо, изумруд, charcoal).
 *   - `strong` — тёмный текст; для светлых фонов (lime, sky-blue, mint,
 *     оранжевый), где белый проваливает контраст по WCAG AA.
 *
 * NB: цвет `<h3>` ставится **явно** через `inkClasses`, потому что
 * `styles/base.css` через `:where(h1..h6) { color: ink-strong }` задаёт
 * заголовкам тёмный цвет напрямую — это перебивает унаследованный
 * цвет родительского `<div>`. Tailwind-утилиты живут в `@layer utilities`
 * и побеждают base-правило по cascade-layer'у.
 */
import { computed } from 'vue';
import type { CaseAccentInk } from '@/content/home-data';

interface Props {
  client: string;
  eyebrow: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  accent: string;
  accentInk?: CaseAccentInk;
}

const props = withDefaults(defineProps<Props>(), {
  accentInk: 'inverse',
});

interface InkPalette {
  container: string;
  client: string;
  eyebrow: string;
  title: string;
  body: string;
}

// Палитры подобраны под WCAG AA на брендовых фонах:
// - eyebrow держим на 85% opacity «своего» чернильного цвета — это
//   сохраняет визуальную иерархию (eyebrow тише заголовка), но
//   проходит контраст даже на ярких lime/sky/mint;
// - `text-ink-muted` (#6b7a8f) для eyebrow в strong-палитре намеренно
//   не используем — на ярких брендах он проваливался до ~1.6–2.7:1;
// - body выводим без opacity, чтобы мелкий текст (text-sm) держал
//   максимально достижимый контраст с фоном.
const INK_PALETTES: Readonly<Record<CaseAccentInk, InkPalette>> = {
  inverse: {
    container: 'text-ink-inverse',
    client: 'text-ink-inverse',
    eyebrow: 'text-ink-inverse/85',
    title: 'text-ink-inverse',
    body: 'text-ink-inverse',
  },
  strong: {
    container: 'text-ink-strong',
    client: 'text-ink-strong',
    eyebrow: 'text-ink-strong/85',
    title: 'text-ink-strong',
    body: 'text-ink-strong',
  },
};

const ink = computed<InkPalette>(() => INK_PALETTES[props.accentInk]);
</script>
